import * as React from "react";
import { makeStyles, shorthands } from "@fluentui/react-components";
import { REFINED } from "../../risikoanalyseStep/tokens";
import { FieldRow, LabeledInput, LabeledSelect } from "../formPrimitives";
import { LegalFormSelect } from "./LegalFormSelect";
import { FirmaBranchenFeld } from "../../relationsInline/ContactRefField";
import { FirmendatenBefuellenDialog } from "../../FirmendatenBefuellenDialog";
import { gemerktesLogo, logoMerken, logoVergessen } from "../../../utils/logoVorschlag";
import type { ContactRef } from "../../relationsInline/types";
import { apiUrl } from "../../../utils/api";
import { useEntprellteSuche, holeJsonListe } from "../../../hooks/useEntprellteSuche";
import { SuchStatus } from "./SuchStatus";
import { PhoneInput } from "./PhoneInput";
import { VollanlageWeiterKnopf } from "../VollanlageWeiter";

export interface VereinEintrag {
  id: string;
  vereinName: string;
  vereinContactId?: number;
  hatMitgliedschaft: boolean;
  funktion: string;
  isNew?: boolean;
  ref?: ContactRef;
}

const FUNKTION_OPTIONEN = [
  { value: "Vorstand / Vorsitzender", label: "Vorstand / Vorsitzender" },
  { value: "Kassenwart / Schatzmeister", label: "Kassenwart / Schatzmeister" },
  { value: "Schriftführer", label: "Schriftführer" },
  { value: "Beirat / Ausschuss", label: "Beirat / Ausschuss" },
  { value: "Sponsor / Förderer", label: "Sponsor / Förderer" },
  { value: "Sonstige Funktion", label: "Sonstige Funktion" },
];

const SearchIcon = (
  <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.8}>
    <circle cx="8.5" cy="8.5" r="5.5" />
    <path d="M13 13l4.5 4.5" />
  </svg>
);

const useStyles = makeStyles({
  list: {
    display: "flex",
    flexDirection: "column",
  },
  item: {
    display: "flex",
    flexDirection: "column",
    rowGap: "14px",
    position: "relative",
  },
  divider: {
    height: "1px",
    backgroundColor: REFINED.line,
    ...shorthands.margin("20px", "0"),
    ...shorthands.borderWidth("0"),
  },
  removeBtn: {
    ...shorthands.border("1px", "solid", REFINED.err),
    ...shorthands.borderRadius("8px"),
    backgroundColor: REFINED.white,
    color: REFINED.err,
    fontFamily: REFINED.fontUI,
    fontWeight: 600,
    fontSize: "0.82rem",
    padding: "6px 12px",
    cursor: "pointer",
    ":hover": { backgroundColor: REFINED.errBg },
  },
  rowActions: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: "12px",
  },
  ghostBtn: {
    ...shorthands.border("1.5px", "solid", REFINED.aub),
    ...shorthands.borderRadius("10px"),
    backgroundColor: REFINED.white,
    color: REFINED.aub,
    fontFamily: REFINED.fontUI,
    fontWeight: 600,
    fontSize: "0.88rem",
    padding: "8px 16px",
    cursor: "pointer",
    transitionProperty: "background-color, color",
    transitionDuration: "140ms",
    ":hover": { backgroundColor: REFINED.aub50 },
    ":disabled": { opacity: 0.5, cursor: "not-allowed" },
  },
  radioGroup: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "16px",
    fontFamily: REFINED.fontUI,
    fontSize: "0.9rem",
    color: REFINED.ink,
    marginTop: "6px",
  },
  radioLabel: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    cursor: "pointer",
    "& input[type='radio']": {
      accentColor: REFINED.aub,
      cursor: "pointer",
    },
  },
  fieldLabel: {
    fontFamily: REFINED.fontUI,
    fontWeight: 600,
    fontSize: "0.875rem",
    color: REFINED.ink,
  },
  searchWrap: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  searchInput: {
    width: "100%",
    height: "38px",
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: "34px",
    paddingRight: "12px",
    ...shorthands.borderRadius("10px"),
    ...shorthands.border("1px", "solid", REFINED.line),
    fontFamily: REFINED.fontUI,
    fontSize: "0.92rem",
    color: REFINED.ink,
    backgroundColor: REFINED.white,
    outlineStyle: "none",
    boxSizing: "border-box",
    ":focus": {
      ...shorthands.borderColor(REFINED.aub),
    },
  },
  searchIcon: {
    position: "absolute",
    left: "10px",
    display: "flex",
    alignItems: "center",
    color: REFINED.ink3,
    pointerEvents: "none",
  },
  searchMsg: {
    fontFamily: REFINED.fontUI,
    fontSize: "0.8rem",
    color: REFINED.ink3,
    marginTop: "2px",
  },
  resultsList: {
    position: "absolute",
    zIndex: 40,
    top: "calc(100% + 4px)",
    left: 0,
    right: 0,
    maxHeight: "220px",
    overflowY: "auto",
    backgroundColor: REFINED.white,
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("10px"),
    boxShadow: REFINED.cardShadow,
    paddingTop: "4px",
    paddingBottom: "4px",
  },
  resultRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "12px",
    paddingRight: "12px",
    ...shorthands.borderWidth("0"),
    ...shorthands.borderStyle("none"),
    backgroundColor: "transparent",
    cursor: "pointer",
    textAlign: "left",
    ":hover": { backgroundColor: REFINED.aub50 },
  },
  resultName: {
    fontFamily: REFINED.fontUI,
    fontSize: "0.9rem",
    fontWeight: 600,
    color: REFINED.ink,
  },
  resultType: {
    fontFamily: REFINED.fontUI,
    fontSize: "0.78rem",
    color: REFINED.ink3,
  },
  newLink: {
    backgroundColor: "transparent",
    ...shorthands.borderWidth("0"),
    ...shorthands.borderStyle("none"),
    color: REFINED.aub,
    fontFamily: REFINED.fontUI,
    fontSize: "0.85rem",
    fontWeight: 600,
    cursor: "pointer",
    paddingTop: "4px",
    paddingBottom: "4px",
    paddingLeft: 0,
    paddingRight: 0,
    textAlign: "left",
    alignSelf: "flex-start",
    ":hover": { textDecorationLine: "underline" },
  },
  switchLink: {
    backgroundColor: "transparent",
    ...shorthands.borderWidth("0"),
    ...shorthands.borderStyle("none"),
    color: REFINED.aub,
    fontFamily: REFINED.fontUI,
    fontSize: "0.82rem",
    fontWeight: 600,
    cursor: "pointer",
    paddingTop: "2px",
    paddingBottom: "2px",
    paddingLeft: 0,
    paddingRight: 0,
    textAlign: "left",
    ":hover": { textDecorationLine: "underline" },
  },
  chip: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingTop: "7px",
    paddingBottom: "7px",
    paddingLeft: "12px",
    paddingRight: "12px",
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("10px"),
    backgroundColor: REFINED.fieldBg,
    height: "38px",
    boxSizing: "border-box",
  },
  chipName: {
    fontFamily: REFINED.fontUI,
    fontWeight: 600,
    fontSize: "0.9rem",
    color: REFINED.ink,
  },
  chipChange: {
    backgroundColor: "transparent",
    ...shorthands.borderWidth("0"),
    ...shorthands.borderStyle("none"),
    color: REFINED.aub,
    fontWeight: 600,
    fontSize: "0.82rem",
    cursor: "pointer",
    ":hover": { textDecorationLine: "underline" },
  },
});

export function createEmptyVerein(): VereinEintrag {
  return {
    id: "verein-" + Date.now() + "-" + Math.random().toString(36).substring(2, 6),
    vereinName: "",
    hatMitgliedschaft: false,
    funktion: "",
  };
}

/**
 * Filtert Suchtreffer strikt auf Vereine anhand ihrer Rechtsform
 * (e.V., nicht eingetragen, gemeinnützig bzw. Verein).
 */
export function isVereinTreffer(c: any): boolean {
  if (c?.type?.type !== "Firma") return false;

  const lf = String(c?.legalForm?.legalForm ?? c?.rechtsform ?? "").trim().toLowerCase();
  const kuerzel = String(c?.legalForm?.kuerzel ?? "").trim().toLowerCase();
  const formCombined = `${lf} ${kuerzel}`.trim();

  // Nicht-Vereine (z. B. gGmbH, GmbH, AG, KG, UG, GbR) ausschließen
  if (/\b(ggmbh|gmbh|ag|kg|ug|ohg|partg|gbr)\b/.test(formCombined)) {
    return false;
  }

  // 1. Rechtsform enthält e.V., nicht eingetragener Verein, gemeinnützig oder Verein
  if (
    formCombined.includes("e.v") ||
    formCombined.includes("e. v") ||
    formCombined.includes("nicht eingetragen") ||
    formCombined.includes("n.e.v") ||
    formCombined.includes("gemeinnützig") ||
    formCombined.includes("verein")
  ) {
    return true;
  }

  // 2. Altbestand ohne erfasste Rechtsform: Name enthält e. V. oder Verein
  if (!lf && !kuerzel) {
    const name = String(c?.displayName || c?.name || c?.lastname || "").toLowerCase();
    if (/\be\.?\s?v\.?\b/.test(name) || /\bverein\b/.test(name)) {
      return true;
    }
  }

  return false;
}

interface VereinItemRowProps {
  eintrag: VereinEintrag;
  onUpdate: (patch: Partial<VereinEintrag>) => void;
  onRemove: () => void;
  canRemove: boolean;
  onOpenBefuellen: (eintragId: string) => void;
  ohneFunktion?: boolean;
  vereinLabel?: string;
}

const VereinItemRow: React.FC<VereinItemRowProps> = ({
  eintrag,
  onUpdate,
  onRemove,
  canRemove,
  onOpenBefuellen,
  ohneFunktion = false,
  vereinLabel = "Verein",
}) => {
  const s = useStyles();
  const [searchTerm, setSearchTerm] = React.useState(eintrag.vereinName || "");
  const [openDropdown, setOpenDropdown] = React.useState(false);

  // Debounced Suche nach Vereinen im Bestand (gefiltert auf e.V., nicht eingetragen, gemeinnützig)
  // Entprellte Suche — Lade-Anzeige endet immer, Fehler sind sichtbar und
  // wiederholbar (useEntprellteSuche).
  const suche = useEntprellteSuche<any>(
    searchTerm,
    !eintrag.vereinContactId && !eintrag.isNew,
    async (term, signal) => {
      const list = await holeJsonListe(apiUrl("contact", {
        search: term,
        limit: "50",
        filters: JSON.stringify({ Typ: "Firma" }),
        // Vereinssuche nur im Namen, schlanke Form (siehe ArbeitgeberAuswahl).
        nurName: "1",
        // Courté-weit: keine persönlichen Kontakte anderer Nutzer (auch nicht
        // für den admin) — eigene bleiben auffindbar.
        ohneFremdePersoenliche: "1",
        include: "",
      }), signal);
      return list.filter(isVereinTreffer).slice(0, 10);
    },
    { debounceMs: 280 },
  );
  const results = suche.treffer;

  const patchRef = (p: Partial<ContactRef>) => {
    const updatedRef: ContactRef = {
      type: "Firma",
      manual: true,
      ...(eintrag.ref ?? {}),
      ...p,
    };
    onUpdate({
      ref: updatedRef,
      vereinName: updatedRef.lastName ?? eintrag.vereinName,
    });
  };

  const displayNameOf = (c: any) =>
    c.displayName || c.name || [c.firstName, c.lastName].filter(Boolean).join(" ") || "Verein";

  return (
    <div className={s.item}>
      {/* Zeile 1: Verein (Suche/Auswahl) | Besteht eine Mitgliedschaft?
          Bei der Firma (ohneFunktion) entfällt die Frage, die Suche nimmt die
          ganze Breite (User 26.09.2026). */}
      <FieldRow columns={ohneFunktion ? 1 : 2}>
        <div style={{ display: "flex", flexDirection: "column", rowGap: "7px", position: "relative" }}>
          <label className={s.fieldLabel}>{vereinLabel}</label>
          {eintrag.vereinContactId ? (
            <div className={s.chip}>
              <span className={s.chipName}>{eintrag.vereinName}</span>
              <button
                type="button"
                className={s.chipChange}
                onClick={() => {
                  setSearchTerm("");
                  onUpdate({
                    vereinContactId: undefined,
                    vereinName: "",
                    isNew: false,
                    ref: undefined,
                  });
                }}
              >
                ändern
              </button>
            </div>
          ) : eintrag.isNew ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", minHeight: "38px" }}>
              <span style={{ fontSize: "0.88rem", fontWeight: 600, color: REFINED.aub }}>
                Neuer Verein (wird angelegt)
              </span>
              <button
                type="button"
                className={s.switchLink}
                onClick={() => {
                  onUpdate({
                    isNew: false,
                    ref: undefined,
                    vereinName: "",
                  });
                  setSearchTerm("");
                }}
              >
                ← stattdessen bestehenden Verein suchen
              </button>
            </div>
          ) : (
            <>
              <div className={s.searchWrap}>
                <span className={s.searchIcon}>{SearchIcon}</span>
                <input
                  type="text"
                  className={s.searchInput}
                  placeholder="Verein oder Verband suchen …"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setOpenDropdown(true);
                  }}
                  onFocus={() => {
                    if (results.length > 0) setOpenDropdown(true);
                  }}
                  onBlur={() => {
                    setTimeout(() => setOpenDropdown(false), 200);
                  }}
                />
              </div>

              <SuchStatus suche={suche} term={searchTerm} className={s.searchMsg} anzahl={results.length} />

              {openDropdown && results.length > 0 && (
                <div className={s.resultsList}>
                  {results.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      className={s.resultRow}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        const name = displayNameOf(c);
                        onUpdate({
                          vereinContactId: c.id,
                          vereinName: name,
                          isNew: false,
                          ref: undefined,
                        });
                        setSearchTerm(name);
                        setOpenDropdown(false);
                      }}
                    >
                      <span className={s.resultName}>{displayNameOf(c)}</span>
                      <span className={s.resultType}>{c.legalForm?.legalForm || "Firma"}</span>
                    </button>
                  ))}
                </div>
              )}

              <button
                type="button"
                className={s.newLink}
                onClick={() => {
                  const initialName = searchTerm.trim();
                  onUpdate({
                    isNew: true,
                    vereinName: initialName,
                    ref: {
                      type: "Firma",
                      manual: true,
                      lastName: initialName,
                      legalForm: "Eingetragener Verein (e.V.)",
                    },
                  });
                  setOpenDropdown(false);
                }}
              >
                + Kontakt ist noch nicht im System — neu eintippen
              </button>
            </>
          )}
        </div>

        {!ohneFunktion && (
          <div style={{ display: "grid", rowGap: "7px" }}>
            <span className={s.fieldLabel}>Besteht eine Mitgliedschaft?</span>
            <div className={s.radioGroup}>
              <label className={s.radioLabel}>
                <input
                  type="radio"
                  name={`mitglied-${eintrag.id}`}
                  checked={eintrag.hatMitgliedschaft === true}
                  onChange={() => {
                    onUpdate({
                      hatMitgliedschaft: true,
                      ...(eintrag.funktion === "Mitglied" ? { funktion: "" } : {}),
                    });
                  }}
                />
                <span>Ja</span>
              </label>
              <label className={s.radioLabel}>
                <input
                  type="radio"
                  name={`mitglied-${eintrag.id}`}
                  checked={eintrag.hatMitgliedschaft === false}
                  onChange={() => onUpdate({ hatMitgliedschaft: false })}
                />
                <span>Nein</span>
              </label>
            </div>
          </div>
        )}
      </FieldRow>

      {/* Zeile 2: Funktion | ggf. Entfernen-Button (bei Firmen-Mitgliedschaften entfällt die Funktion) */}
      {!ohneFunktion && (
        <FieldRow columns={2}>
          <LabeledSelect
            label="Funktion"
            name={`funktion-${eintrag.id}`}
            value={eintrag.funktion}
            onChange={(v) => onUpdate({ funktion: v })}
            options={FUNKTION_OPTIONEN}
          />
          {canRemove ? (
            <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "flex-end", height: "100%", paddingBottom: "2px" }}>
              <button
                type="button"
                className={s.removeBtn}
                onClick={onRemove}
                title="Verein entfernen"
              >
                Entfernen
              </button>
            </div>
          ) : (
            <div />
          )}
        </FieldRow>
      )}

      {/* Wenn ohneFunktion aktiv und mehrere Einträge: Entfernen-Button unter Zeile 1 */}
      {ohneFunktion && canRemove && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "2px" }}>
          <button
            type="button"
            className={s.removeBtn}
            onClick={onRemove}
            title="Verein entfernen"
          >
            Entfernen
          </button>
        </div>
      )}

      {/* Zeilen für NEUEN Verein — erscheinen direkt unter Funktion */}
      {eintrag.isNew && (
        <div style={{ marginTop: "4px", display: "flex", flexDirection: "column", rowGap: "14px" }}>
          {/* Name | Rechtsform (e.V. vorausgewählt) */}
          <FieldRow columns={2}>
            <LabeledInput
              label="Name"
              name={`verein-name-${eintrag.id}`}
              required
              value={eintrag.ref?.lastName ?? eintrag.vereinName}
              onChange={(v) => patchRef({ lastName: v })}
              placeholder="z. B. Aachener Sportverein e. V."
            />
            <LegalFormSelect
              label="Rechtsform"
              name={`verein-legalform-${eintrag.id}`}
              required
              value={eintrag.ref?.legalForm || "Eingetragener Verein (e.V.)"}
              onChange={(v, id) => patchRef({ legalForm: v, legalFormId: id ? Number(id) : undefined })}
            />
          </FieldRow>

          {/* Branche | Branchengruppen */}
          <FirmaBranchenFeld
            value={eintrag.ref ?? { type: "Firma", manual: true }}
            onPatch={patchRef}
            idBase={`verein-${eintrag.id}`}
          />

          {/* Telefon | Mobil */}
          <FieldRow columns={2}>
            <PhoneInput actionButton={false} kind="landline" geschaeftlich
              label="Telefon"
              name={`verein-tel-${eintrag.id}`}
              value={eintrag.ref?.phone ?? ""}
              onChange={(v) => patchRef({ phone: v })}
            />
            <PhoneInput actionButton={false} kind="mobile" geschaeftlich
              label="Mobil"
              name={`verein-mob-${eintrag.id}`}
              value={eintrag.ref?.mobile ?? ""}
              onChange={(v) => patchRef({ mobile: v })}
            />
          </FieldRow>

          {/* E-Mail | Website */}
          <FieldRow columns={2}>
            <LabeledInput
              label="E-Mail"
              name={`verein-mail-${eintrag.id}`}
              value={eintrag.ref?.email ?? ""}
              onChange={(v) => patchRef({ email: v })}
            />
            <LabeledInput
              label="Website"
              name={`verein-web-${eintrag.id}`}
              value={eintrag.ref?.web ?? ""}
              onChange={(v) => patchRef({ web: v })}
              placeholder="https://…"
            />
          </FieldRow>

          {/* Daten befüllen Button rechts */}
          <FieldRow columns={2}>
            <div />
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                type="button"
                className={s.ghostBtn}
                disabled={!String(eintrag.ref?.lastName ?? "").trim() && !String(eintrag.ref?.web ?? "").trim()}
                onClick={() => onOpenBefuellen(eintrag.id)}
                title="Anschrift, Rechtsform und Rufnummern aus dem Impressum übernehmen"
              >
                Daten befüllen
              </button>
            </div>
          </FieldRow>

          {/* Vollanlage des Vereins (User 02.10.2026): danach ist er angelegt
              und steht hier als gewählter Verein — die Mitgliedschaft entsteht
              beim Speichern des Kontakts mit seiner Id. */}
          <VollanlageWeiterKnopf
              kontakt={() => ({
                type: "Firma",
                manual: true,
                ...(eintrag.ref ?? {}),
                lastName: eintrag.ref?.lastName ?? eintrag.vereinName,
              })}
              verein
              onCreated={({ contactId, ref }) => {
                const name = ref.displayName || ref.lastName || eintrag.vereinName;
                onUpdate({ vereinContactId: contactId, vereinName: name, isNew: false, ref: undefined });
                setSearchTerm(name);
              }}
            />
        </div>
      )}
    </div>
  );
};

export interface VereinMitgliedschaftenProps {
  value: VereinEintrag[];
  onChange: (next: VereinEintrag[]) => void;
  ohneFunktion?: boolean;
  /**
   * Beschriftung des Suchfelds. Die Firmenanlage fragt nach
   * „Verein/Verband/Interessensgemeinschaft" (User 28.09.2026) — eine Firma
   * ist eher in Verbänden als in Vereinen Mitglied.
   */
  vereinLabel?: string;
}

export const VereinMitgliedschaften: React.FC<VereinMitgliedschaftenProps> = ({
  value,
  onChange,
  ohneFunktion = false,
  vereinLabel,
}) => {
  const s = useStyles();
  const [befuellenId, setBefuellenId] = React.useState<string | null>(null);

  // Immer mindestens ein Eintrag vorausgefüllt anzeigen (nicht erst nach Button-Klick)
  React.useEffect(() => {
    if (!value || value.length === 0) {
      onChange([createEmptyVerein()]);
    }
  }, [value, onChange]);

  const addEintrag = () => {
    onChange([...value, createEmptyVerein()]);
  };

  const updateEintrag = (id: string, patch: Partial<VereinEintrag>) => {
    onChange(value.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  };

  const removeEintrag = (id: string) => {
    if (value.length <= 1) return;
    onChange(value.filter((it) => it.id !== id));
  };

  const activeBefuellenEintrag = value.find((it) => it.id === befuellenId);

  return (
    <div className={s.list}>
      {value.map((eintrag, idx) => (
        <React.Fragment key={eintrag.id}>
          {idx > 0 && <hr className={s.divider} />}
          <VereinItemRow
            eintrag={eintrag}
            onUpdate={(patch) => updateEintrag(eintrag.id, patch)}
            onRemove={() => removeEintrag(eintrag.id)}
            canRemove={value.length > 1}
            onOpenBefuellen={(id) => setBefuellenId(id)}
            ohneFunktion={ohneFunktion}
            vereinLabel={vereinLabel}
          />
        </React.Fragment>
      ))}

      {/* Button: Weiteren Verein/Mitgliedschaft hinzufügen — rechtsbündig */}
      <div className={s.rowActions}>
        <button
          type="button"
          className={s.ghostBtn}
          onClick={addEintrag}
          title="Weiteren Verein oder weitere Mitgliedschaft erfassen"
        >
          + Weiteren Verein/Mitgliedschaft hinzufügen
        </button>
      </div>

      {activeBefuellenEintrag && (
        <FirmendatenBefuellenDialog
          open={true}
          eingabe={{
            name: String(activeBefuellenEintrag.ref?.lastName ?? activeBefuellenEintrag.vereinName ?? "").trim(),
            ort: null,
            plz: null,
            strasse: null,
            website: activeBefuellenEintrag.ref?.web || null,
            email: activeBefuellenEintrag.ref?.email || null,
          }}
          vorhanden={{
            name: String(activeBefuellenEintrag.ref?.lastName ?? activeBefuellenEintrag.vereinName ?? "").trim() || undefined,
            website: activeBefuellenEintrag.ref?.web || undefined,
            email: activeBefuellenEintrag.ref?.email || undefined,
            phone: activeBefuellenEintrag.ref?.phone || undefined,
            rechtsform: activeBefuellenEintrag.ref?.legalForm || undefined,
          }}
          onClose={() => setBefuellenId(null)}
          // Logo-Vorschlag: gemerkt, übernommen wird es nach dem Anlegen des
          // Vereins (createContactFromRef/persistRelations → logoNachreichen).
          logo={{
            vorhanden: !!gemerktesLogo(activeBefuellenEintrag.ref?.logoVorschlag),
            onJa: (bild) => {
              logoVergessen(activeBefuellenEintrag.ref?.logoVorschlag);
              updateEintrag(activeBefuellenEintrag.id, {
                ref: {
                  ...activeBefuellenEintrag.ref,
                  type: "Firma",
                  manual: true,
                  logoVorschlag: logoMerken(bild),
                },
              });
            },
          }}
          onUebernehmen={(felder) => {
            updateEintrag(activeBefuellenEintrag.id, {
              vereinName: felder.name || activeBefuellenEintrag.vereinName,
              ref: {
                ...activeBefuellenEintrag.ref,
                type: "Firma",
                manual: true,
                ...(felder.name ? { lastName: felder.name } : {}),
                ...(felder.phone ? { phone: felder.phone } : {}),
                ...(felder.email ? { email: felder.email } : {}),
                ...(felder.website ? { web: felder.website } : {}),
                ...(felder.rechtsform ? { legalForm: felder.rechtsform } : {}),
              },
            });
            setBefuellenId(null);
          }}
        />
      )}
    </div>
  );
};
